import { useEffect, useState } from "react";
import { BookOpen, Download } from "lucide-react";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";

/**
 * Botón de descarga del manual operativo del usuario actual.
 * Consulta /api/manuals (filtra por permisos) y baja el primero disponible.
 * Si el usuario no tiene ningún manual asociado, no renderiza nada.
 */
export default function DownloadManualButton({ variant = "card", className = "" }) {
    const [manual, setManual] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let cancel = false;
        api.get("/manuals")
            .then((r) => {
                if (cancel) return;
                const list = Array.isArray(r?.data) ? r.data : [];
                if (list.length) setManual(list[0]);
            })
            .catch(() => {});
        return () => { cancel = true; };
    }, []);

    const download = async () => {
        if (!manual || busy) return;
        setBusy(true);
        try {
            const res = await fetch(`${API_BASE}/manuals/${manual.role}`, { credentials: "include" });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = manual.filename || `FindMed-Manual-${manual.role}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("Manual descargado");
        } catch {
            toast.error("No se pudo descargar el manual");
        } finally {
            setBusy(false);
        }
    };

    if (!manual) return null;

    if (variant === "inline") {
        return (
            <button
                type="button"
                onClick={download}
                disabled={busy}
                data-testid="download-my-manual-btn"
                className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-sky-700 hover:text-sky-900 transition ${className}`}
            >
                <Download className="w-3.5 h-3.5" />
                {busy ? "Descargando..." : "Mi manual PDF"}
            </button>
        );
    }

    return (
        <button
            type="button"
            onClick={download}
            disabled={busy}
            data-testid="download-my-manual-btn"
            className={`fm-card p-3 w-full flex items-center justify-between border-l-4 border-l-sky-500 hover:bg-sky-50/50 transition ${className}`}
        >
            <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-sky-100 text-sky-700 flex items-center justify-center">
                    <BookOpen className="w-4 h-4" />
                </div>
                <div className="text-left">
                    <div className="text-xs font-bold uppercase tracking-wider text-slate-900">
                        Tu manual de operación
                    </div>
                    <div className="text-[10px] text-slate-500 font-mono">
                        {manual.title} · PDF
                    </div>
                </div>
            </div>
            <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-sky-700">
                <Download className="w-3.5 h-3.5" />
                {busy ? "..." : "Descargar"}
            </div>
        </button>
    );
}
